import { useEffect, useState, useMemo, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useGlobalSupervisionData — data + state hook backing GlobalSupervisionTab.
 *
 * Encapsulates :
 *   - the /api/admin/overview/all fetch + 90s auto-refresh
 *   - search, filter, sort, establishment filter state
 *   - derived aggregates (totalFleet, negEquity, cashAlerts, avgDays, playing)
 *   - filtered + sorted list
 *   - establishment options list
 *
 * Returns everything the UI needs. Pure state hook — no JSX.
 *
 * 29/05/2026 — extracted from the 409-line GlobalSupervisionTab monolith.
 */
export default function useGlobalSupervisionData(token, opts = {}) {
  const { hideTest = false } = opts;
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, playing_count: 0, masters_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [expandedKey, setExpandedKey] = useState(null);
  const [establishmentFilter, setEstablishmentFilter] = useState('__all__');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API}/api/admin/overview/all`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        setError('Erreur de chargement');
        setItems([]);
      } else {
        const data = await resp.json();
        setItems(Array.isArray(data?.items) ? data.items : []);
        setMeta({
          total: data?.total || 0,
          playing_count: data?.playing_count || 0,
          masters_count: data?.masters_count || 0,
        });
      }
    } catch (e) {
      setError(e.message || 'Erreur réseau');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  // Auto-refresh every 90 s.
  useEffect(() => {
    if (!token) return undefined;
    const id = setInterval(fetchAll, 90000);
    return () => clearInterval(id);
  }, [token, fetchAll]);

  const isTestItem = useCallback((it) => {
    const name = (it.license?.holder_name || '').toUpperCase();
    const email = (it.license?.holder_email || '').toUpperCase();
    return name.startsWith('TEST_') || name.startsWith('TEST ') || email.startsWith('TEST_') || email.includes('@TEST.');
  }, []);

  const visibleItems = useMemo(
    () => (hideTest ? items.filter((it) => !isTestItem(it)) : items),
    [items, hideTest, isTestItem],
  );

  const aggregated = useMemo(() => {
    let totalFleet = 0;
    let negEquity = 0;
    let cashAlerts = 0;
    let avgDays = 0;
    let playing = 0;
    visibleItems.forEach(it => {
      const s = it.snapshot;
      if (!s) return;
      playing += 1;
      totalFleet += s.fleet_total || 0;
      avgDays += s.total_days || 0;
      if ((s.equity || 0) < 0) negEquity += 1;
      if ((s.treasury || 0) < 5000) cashAlerts += 1;
    });
    return {
      totalFleet,
      negEquity,
      cashAlerts,
      avgDays: playing > 0 ? Math.round(avgDays / playing) : 0,
      playing,
    };
  }, [visibleItems]);

  const filteredSorted = useMemo(() => {
    const s = (search || '').toLowerCase().trim();
    let arr = visibleItems.filter(it => {
      if (s) {
        const hay = [
          it.license?.holder_name || '',
          it.license?.license_key || '',
          it.snapshot?.company_name || '',
          it.cohort_name || '',
          it.establishment || '',
        ].join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (establishmentFilter !== '__all__') {
        if (establishmentFilter === '__none__') {
          if (it.establishment) return false;
        } else if (it.establishment !== establishmentFilter) {
          return false;
        }
      }
      if (filter === 'playing') return !!it.snapshot && (it.snapshot.total_days || 0) > 0;
      if (filter === 'unplayed') return !it.snapshot;
      if (filter === 'alerts') {
        if (!it.snapshot) return false;
        return (it.snapshot.equity || 0) < 0 || (it.snapshot.treasury || 0) < 5000;
      }
      return true;
    });
    arr = [...arr].sort((a, b) => {
      const sa = a.snapshot;
      const sb = b.snapshot;
      switch (sortBy) {
        case 'name':
          return (a.license?.holder_name || '').localeCompare(b.license?.holder_name || '', 'fr');
        case 'cohort':
          return (a.cohort_name || 'zzz').localeCompare(b.cohort_name || 'zzz', 'fr');
        case 'treasury':
          return (sb?.treasury || -Infinity) - (sa?.treasury || -Infinity);
        case 'reputation':
          return (sb?.reputation || -Infinity) - (sa?.reputation || -Infinity);
        case 'days':
          return (sb?.total_days || 0) - (sa?.total_days || 0);
        case 'playtime':
          return (sb?.play_time_seconds || 0) - (sa?.play_time_seconds || 0);
        case 'recent':
        default: {
          const da = sa?.updated_at || '';
          const db = sb?.updated_at || '';
          return db.localeCompare(da);
        }
      }
    });
    return arr;
  }, [visibleItems, search, filter, sortBy, establishmentFilter]);

  const establishmentOptions = useMemo(() => {
    const set = new Set();
    items.forEach(it => { if (it.establishment) set.add(it.establishment); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [items]);

  return {
    items, meta, loading, error,
    search, setSearch,
    filter, setFilter,
    sortBy, setSortBy,
    expandedKey, setExpandedKey,
    establishmentFilter, setEstablishmentFilter,
    aggregated,
    filteredSorted,
    establishmentOptions,
  };
}
